import React, { useEffect, useState, useCallback } from 'react';
import { availabilityAPI } from '../utils/api';

const STATUSES = [
  { value: 'available', label: 'Available', dot: 'bg-emerald-500' },
  { value: 'busy',      label: 'Busy',      dot: 'bg-amber-500' },
  { value: 'away',      label: 'Away',      dot: 'bg-slate-400' },
  { value: 'offline',   label: 'Offline',   dot: 'bg-slate-300' },
];

/**
 * AvailabilityToggle — Slack-style availability picker on the user avatar.
 * Heartbeats every 5 min when status='available' so the idle rule doesn't
 * flip them to 'away' while they're actively using the app.
 */
export default function AvailabilityToggle({ compact = false }) {
  const [status, setStatus] = useState('available');
  const [open, setOpen]     = useState(false);

  // Initial load
  useEffect(() => {
    let cancelled = false;
    availabilityAPI.getMine()
      .then((res) => { if (!cancelled) setStatus(res.data?.stored_status || res.data?.status || 'available'); })
      .catch(() => {}); // silent — defaults to available
    return () => { cancelled = true; };
  }, []);

  // Heartbeat every 5 min while status is 'available'
  useEffect(() => {
    if (status !== 'available') return;
    const id = setInterval(() => {
      availabilityAPI.setMine('available').catch(() => {});
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [status]);

  const change = useCallback(async (next) => {
    setStatus(next);   // optimistic
    setOpen(false);
    try {
      await availabilityAPI.setMine(next);
    } catch {
      // Roll back on error — fetch the canonical state
      try {
        const res = await availabilityAPI.getMine();
        setStatus(res.data?.stored_status || 'available');
      } catch {}
    }
  }, []);

  const current = STATUSES.find(s => s.value === status) || STATUSES[0];

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className={`inline-flex items-center gap-1.5 ${compact ? 'px-2 py-1' : 'px-2.5 py-1.5'} rounded-md hover:bg-slate-100 transition-colors`}
        title={`You are ${current.label}`}
      >
        <span className={`w-2 h-2 rounded-full ${current.dot}`} />
        {!compact && <span className="text-xs font-medium text-slate-600">{current.label}</span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-44 bg-white border border-slate-200 rounded-md shadow-lg z-20 py-1">
            {STATUSES.map(s => (
              <button
                key={s.value}
                onClick={() => change(s.value)}
                className={`w-full px-3 py-2 text-left text-xs flex items-center gap-2 hover:bg-slate-50 ${s.value === status ? 'bg-violet-50' : ''}`}
              >
                <span className={`w-2 h-2 rounded-full ${s.dot}`} />
                <span className="text-slate-700">{s.label}</span>
                {s.value === status && <span className="ml-auto text-violet-600">✓</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
